import {produce} from 'immer';

import {assert} from 'Asserts';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {AutomatedMappingResultFieldAndValue} from 'processes/dao/ProcessManagementDAO';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {createEmptyTaskTableFromSchemaWithEmptyRows} from 'processes/tables/createEmptyTaskTableFromSchema';
import {createOutputTableWithEmptyRows} from 'processes/tables/createOutputTable';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import APIUtil from 'util/APIUtil';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

function propagateTypedValueToTableColumn<
  T extends ProcessTaskStructuredTable | ProcessOutputStructuredTable,
>(typedValue: TypedValue, rowIdx: number, destinationColumnIndex: number, table: T): T {
  return produce(table, (draftTable) => {
    const updatedRow = produce(table.rows[rowIdx], (draftRow) => {
      draftRow.cells[destinationColumnIndex] = {
        text: '',
        typedValue,
      };
    });
    draftTable.rows = [updatedRow];
  });
}

function getStructuredTable(
  schema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL,
  numRows: number,
  selectorsById: SchemaFieldSelectorsDict
): ProcessTaskStructuredTable | ProcessOutputStructuredTable {
  if (APIUtil.isProcessTaskSchema(schema.id)) {
    return createEmptyTaskTableFromSchemaWithEmptyRows({
      name: schema.name,
      numRows,
      taskSchema: {...schema, statusLifecycle: null},
      tableLocation: ProcessTableLocation.FIELD_MAPPING_MODAL,
      locked: false,
      selectorsById,
    });
  } else {
    return createOutputTableWithEmptyRows({
      numRows,
      outputSchema: schema,
      metadata: {tableLocation: ProcessTableLocation.FIELD_MAPPING_MODAL},
      selectorsById,
    });
  }
}

export function buildTableForAutomatedMappingResults(
  schema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL,
  fieldsAndValuesForRows: Array<Array<AutomatedMappingResultFieldAndValue>>,
  selectorsById: SchemaFieldSelectorsDict
): ProcessTaskStructuredTable | ProcessOutputStructuredTable {
  let table = getStructuredTable(schema, fieldsAndValuesForRows.length, selectorsById);

  fieldsAndValuesForRows.forEach((fieldsAndValuds, rowIdx) => {
    for (const {schemaField, value} of fieldsAndValuds) {
      const destinationColumnIndex: number = table.columns.findIndex(
        (column) => column.schemaFieldApiId === schemaField.apiId
      );
      assert(destinationColumnIndex >= 0, `Expected table to have ${schemaField.apiId}`);
      table = propagateTypedValueToTableColumn(value, rowIdx, destinationColumnIndex, table);
    }
  });
  return table;
}

export default function buildTableForAutomatedMappingResult(
  schema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL,
  fieldsAndValues: Array<AutomatedMappingResultFieldAndValue>,
  selectorsById: SchemaFieldSelectorsDict
): ProcessTaskStructuredTable | ProcessOutputStructuredTable {
  let table = getStructuredTable(schema, 1, selectorsById);

  for (const {schemaField, value} of fieldsAndValues) {
    const destinationColumnIndex: number = table.columns.findIndex(
      (column) => column.schemaFieldApiId === schemaField.apiId
    );
    assert(destinationColumnIndex >= 0, `Expected table to have ${schemaField.apiId}`);

    table = propagateTypedValueToTableColumn(value, 0, destinationColumnIndex, table);
  }
  return table;
}
